// src/components/SettingsDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Grid,
  TextField,
  Button,
  Avatar,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
  Paper,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Select,
  InputLabel,
  FormControl,
  useTheme,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import EmailIcon from "@mui/icons-material/Email";
import SecurityIcon from "@mui/icons-material/Security";
import TuneIcon from "@mui/icons-material/Tune";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseIcon from "@mui/icons-material/Close";

import { authPost } from "../lib/authApi";

// ───────────────────────────── helpers ─────────────────────────────
const S = (v) => (v ?? "");
const busyBtn = (loading, label) =>
  loading ? (
    <>
      <CircularProgress size={18} style={{ marginRight: 8 }} /> {label}
    </>
  ) : (
    label
  );

const TabPanel = ({ when, children, sx }) =>
  when ? <Box sx={{ p: 3, ...sx }}>{children}</Box> : null;

function readStoredUser() {
  try {
    const raw = localStorage.getItem("api_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// ───────────────────────────── main ─────────────────────────────
export default function SettingsDialog({ open, onClose, startPage }) {
  const theme = useTheme();

  // tabs
  const [tab, setTab] = useState("profile");
  useEffect(() => {
    if (open && startPage) setTab(startPage);
  }, [open, startPage]);

  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  // user context (from backend login payload stored locally)
  const [user, setUser] = useState(null);

  // Fake org/members until you wire backend endpoints for them.
  // We still make invite work (calls /api/invite).
  const [org] = useState({ id: "local", name: "Organization", owner_user_id: "local" });
  const [members, setMembers] = useState([]);

  const isOwner = useMemo(() => {
    // If you add real org logic later, replace this
    return true;
  }, []);

  // Profile (local only for now)
  const [pf, setPf] = useState({
    full_name: "",
    job_title: "",
    phone: "",
    avatar_url: "",
    email: "",
  });

  // Account (local only for now)
  const [acct, setAcct] = useState({
    email: "",
    newPassword: "",
  });

  // Preferences (local only for now)
  const [prefs, setPrefs] = useState({
    theme: "system",
    density: "comfortable",
    timezone: "UTC",
    date_format: "MM/DD/YYYY",
  });

  // Notifications (local only for now)
  const [notif, setNotif] = useState({
    email_general: true,
    email_security: true,
    product_updates: true,
    weekly_digest: false,
  });

  // Manage Users (invite uses backend)
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");

  // ─────────── load baseline on open ───────────
  useEffect(() => {
    if (!open) return;
    setLoading(true);

    try {
      const u = readStoredUser();
      setUser(u);

      setPf((x) => ({
        ...x,
        email: S(u?.email),
        full_name: S(u?.full_name || ""),
      }));

      setAcct((x) => ({
        ...x,
        email: S(u?.email),
      }));
    } catch (e) {
      notify(e?.message || "Failed to load settings", "error");
    } finally {
      setLoading(false);
    }
  }, [open]);

  // ─────────────────────────────────────────────────────────────
  // ✅ addMember uses backend invite flow (/api/invite) WITH JWT
  // ─────────────────────────────────────────────────────────────
  const addMember = async () => {
    if (!inviteEmail) return;

    setLoading(true);
    try {
      // Your backend protects /invite with verifyAppJWT
      // so this MUST include Authorization header -> authPost does that.
      const resp = await authPost("/api/invite", {
        email: inviteEmail,
        role: inviteRole,
      });

      // If your backend returns something like { success: true, member: ... }
      // we’ll just add a local row so UI reflects it immediately.
      setMembers((prev) => [
        ...prev,
        {
          id: crypto.randomUUID?.() || String(Date.now()),
          role: inviteRole,
          auth_email: inviteEmail,
          user_id: null, // pending link
        },
      ]);

      setInviteEmail("");
      setInviteRole("member");
      notify(resp?.message || "Invite sent");
    } catch (err) {
      notify(err?.message || "Invite failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // removeMember requires a backend endpoint (not implemented in your snippet)
  const removeMember = async (id) => {
    // If you add an endpoint later, replace with authDelete(...)
    setMembers((prev) => prev.filter((m) => m.id !== id));
    notify("Member removed (local UI only). Add backend endpoint to persist.", "warning");
  };

  const CardShell = ({ children, sx }) => (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        backdropFilter: "blur(6px)",
        backgroundColor:
          theme.palette.mode === "dark" ? "rgba(15,15,15,0.6)" : "rgba(255,255,255,0.8)",
        borderRadius: 3,
        ...sx,
      }}
    >
      {children}
    </Paper>
  );

  const appVersion = process.env.REACT_APP_VERSION || "dev";

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ px: 2, py: 1.5 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <TuneIcon fontSize="small" />
            <Typography variant="h6">Settings</Typography>
            <Box flex={1} />
            {loading && <CircularProgress size={18} />}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              px: 2,
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": { minHeight: 48 },
            }}
          >
            <Tab icon={<PersonIcon fontSize="small" />} iconPosition="start" label="My Profile" value="profile" />
            <Tab icon={<SecurityIcon fontSize="small" />} iconPosition="start" label="Account" value="account" />
            <Tab icon={<TuneIcon fontSize="small" />} iconPosition="start" label="Preferences" value="preferences" />
            <Tab icon={<EmailIcon fontSize="small" />} iconPosition="start" label="Notifications" value="notifications" />
            <Tab icon={<GroupIcon fontSize="small" />} iconPosition="start" label="Manage Users" value="users" />
            <Tab icon={<HelpOutlineIcon fontSize="small" />} iconPosition="start" label="Help & Support" value="help" />
          </Tabs>

          {/* PROFILE */}
          <TabPanel when={tab === "profile"} sx={{ p: 0 }}>
            <Box sx={{ px: 3, py: 3 }}>
              <CardShell>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm="auto">
                    <Avatar
                      src={S(pf.avatar_url) || undefined}
                      alt={S(pf.full_name) || "User"}
                      sx={{ width: 92, height: 92, fontWeight: 700, fontSize: 28 }}
                    >
                      {S(pf.full_name).slice(0, 1).toUpperCase() || "U"}
                    </Avatar>
                  </Grid>

                  <Grid item xs={12} sm>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Full name"
                          value={S(pf.full_name)}
                          onChange={(e) => setPf((x) => ({ ...x, full_name: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Job title"
                          value={S(pf.job_title)}
                          onChange={(e) => setPf((x) => ({ ...x, job_title: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone"
                          value={S(pf.phone)}
                          onChange={(e) => setPf((x) => ({ ...x, phone: e.target.value }))}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          value={S(pf.email)}
                          InputProps={{ readOnly: true }}
                          helperText="Managed by your account"
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Alert severity="info">
                      Profile fields are currently local-only. If you want persistence like before, we’ll add backend endpoints
                      (e.g. <code>/api/profile</code>) and store in Postgres.
                    </Alert>

                    <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
                      <Button onClick={onClose}>Close</Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardShell>
            </Box>
          </TabPanel>

          {/* ACCOUNT */}
          <TabPanel when={tab === "account"}>
            <Grid container spacing={2} maxWidth={720}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={S(acct.email)}
                  onChange={(e) => setAcct((x) => ({ ...x, email: e.target.value }))}
                  helperText="Account updates need backend endpoints (or Supabase Auth)."
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  type="password"
                  label="New password"
                  value={S(acct.newPassword)}
                  onChange={(e) => setAcct((x) => ({ ...x, newPassword: e.target.value }))}
                  helperText="If you want password changes, we’ll add /api/auth/change-password."
                />
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
              <Button onClick={onClose}>Close</Button>
            </Box>
          </TabPanel>

          {/* PREFERENCES */}
          <TabPanel when={tab === "preferences"}>
            <Grid container spacing={2} maxWidth={720}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Theme"
                  value={S(prefs.theme)}
                  onChange={(e) => setPrefs((x) => ({ ...x, theme: e.target.value }))}
                >
                  <MenuItem value="system">System</MenuItem>
                  <MenuItem value="light">Light</MenuItem>
                  <MenuItem value="dark">Dark</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Density"
                  value={S(prefs.density)}
                  onChange={(e) => setPrefs((x) => ({ ...x, density: e.target.value }))}
                >
                  <MenuItem value="comfortable">Comfortable</MenuItem>
                  <MenuItem value="compact">Compact</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              Preferences are currently local-only. Add backend persistence if needed.
            </Alert>

            <Box display="flex" justifyContent="flex-end" mt={3} gap={1}>
              <Button onClick={onClose}>Close</Button>
            </Box>
          </TabPanel>

          {/* NOTIFICATIONS */}
          <TabPanel when={tab === "notifications"}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!notif.email_general}
                      onChange={(e) => setNotif((x) => ({ ...x, email_general: e.target.checked }))}
                    />
                  }
                  label="General emails"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!notif.email_security}
                      onChange={(e) => setNotif((x) => ({ ...x, email_security: e.target.checked }))}
                    />
                  }
                  label="Security alerts"
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              Notification settings are currently local-only. Add backend persistence if needed.
            </Alert>

            <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
              <Button onClick={onClose}>Close</Button>
            </Box>
          </TabPanel>

          {/* MANAGE USERS */}
          <TabPanel when={tab === "users"}>
            {!org ? (
              <Alert severity="info" icon={<WarningAmberRoundedIcon />}>
                No organization found.
              </Alert>
            ) : (
              <>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="subtitle2">Organization</Typography>
                  <Chip size="small" label={org?.name || `#${org.id}`} variant="outlined" />
                  {isOwner ? (
                    <Chip size="small" icon={<CheckCircleRoundedIcon />} label="Owner" color="success" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Member" variant="outlined" />
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2} maxWidth={720} alignItems="center">
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      label="Invite by email"
                      value={S(inviteEmail)}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="person@example.com"
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                      <InputLabel id="role-label">Role</InputLabel>
                      <Select
                        labelId="role-label"
                        label="Role"
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                      >
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="member">Member</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      startIcon={<AddIcon />}
                      variant="contained"
                      onClick={addMember}
                      disabled={loading || !inviteEmail || !isOwner}
                    >
                      Invite
                    </Button>
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                  Members (local UI list)
                </Typography>

                {members.length === 0 ? (
                  <Typography color="text.secondary">No members yet.</Typography>
                ) : (
                  <List dense>
                    {members.map((m) => (
                      <ListItem
                        key={m.id}
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 2,
                          mb: 1,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar>
                            <PersonIcon />
                          </Avatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={m.auth_email || "Invite pending"}
                          secondary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip size="small" label={m.role} variant="outlined" />
                              {m.user_id ? (
                                <Chip size="small" label="Linked" color="success" variant="outlined" />
                              ) : (
                                <Chip size="small" label="Unlinked" color="warning" variant="outlined" />
                              )}
                            </Box>
                          }
                        />

                        <ListItemSecondaryAction>
                          <Tooltip title="Copy email">
                            <IconButton
                              edge="end"
                              onClick={() => {
                                if (m.auth_email) {
                                  navigator.clipboard.writeText(m.auth_email);
                                  notify("Copied to clipboard");
                                }
                              }}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={isOwner ? "Remove" : "Owner only"}>
                            <span>
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() => removeMember(m.id)}
                                disabled={!isOwner || loading}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                  Invites are now sent through your backend <code>/api/invite</code> using the JWT.
                  If you want members to load from Postgres, we’ll add a <code>GET /api/invite</code> (or org members endpoint)
                  and replace this local list.
                </Alert>
              </>
            )}
          </TabPanel>

          {/* HELP & SUPPORT */}
          <TabPanel when={tab === "help"}>
            <Grid container spacing={2} maxWidth={760}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Help & Support
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Debug Info
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="User ID" value={S(user?.id)} InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Email" value={S(user?.email)} InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="App Version" value={appVersion} InputProps={{ readOnly: true }} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ px: 2 }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={2800}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
